var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"n1765347667089"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "/mallfe";

import * as Sentry from '@sentry/nextjs';
import { dsns } from './sentry.config';
import './sentry-request-wechat-fix';
/**
 * 
 * tracePropagationTargets
 * 这是 Sentry 的 Browser Tracing 配置，用来指定：
👉 对哪些请求附带 trace 上下文（trace headers）
这样 Sentry 才能把：

前端页面加载 → 前端 API 请求 → 后端接口 → 数据库
串成一条完整链路（即 APM trace）。

 */

/**
 * browserTracingIntegration  最实用
 */

/**
 * BrowserProfilingIntegration 的作用

核心目标：统计前端 CPU 和函数执行性能，补充 transaction + span 的耗时分析。

区别于普通 Transaction：

普通 Transaction / Span 主要关注“操作耗时”，比如路由切换、API 请求。

Profiling Integration 则会记录函数级别的性能信息，类似浏览器的 Performance API。

统计内容：

函数调用栈（stack traces）

函数执行时间（inclusive/exclusive time）

页面渲染阶段的耗时

事件循环延迟（Event Loop Lag）

JS CPU 使用情况（哪个函数占用 CPU 最多）

本质上，它是前端的 CPU Profiling，帮助你定位慢函数或瓶颈代码。

采样率是一个 0 到 1 之间的浮点数：

0 → 完全不采集（事件/Transaction 不会被发送）

1 → 全部采集（100% 事件/Transaction 会发送）

0.5 → 50% 采样（每个事件/Transaction 有一半概率被发送）

可以理解为对事件做 随机抽样，降低监控数据量和前端性能开销。
 */

interface TraceSamplingContext {
  parentSampled?: boolean;
  transactionContext?: {
    op?: string;
  };
}

const environment = process.env.ENV || 'local';
const isLocalEnvironment = environment === 'local';
const isProdEnvironment = ['zjga', 'zjpre'].includes(environment);

const detectHostEnvironment = () => {
  if(typeof window === 'undefined') return 'h5';
  if(window.__wxjs_environment === 'miniprogram') return 'miniprogram';
  return 'h5';
};

/**
 * 为 Sentry 事件添加环境标签
 * @param event - Sentry 事件对象
 * @returns 处理后的事件对象
 */
function addEnvironmentTags(event: any) {
  if (typeof window === 'undefined') {
    return event;
  }

  const hostname = window.location.hostname || 'unknown-host';
  const isMobileDomain =
    hostname === 'm.3jyx.cn' ||
    /^m-(dev|st|pre)\.3jyx\.cn$/.test(hostname) ||
    hostname.startsWith('m.');
  const siteType = isMobileDomain ? 'mobile' : 'pc';

  // 检测宿主环境（仅在移动端时）
  const hostEnv = isMobileDomain ? detectHostEnvironment() : 'browser';

  event.tags = {
    ...(event.tags || {}),
    host: hostname,
    site_type: siteType,
    host_env: hostEnv,
  };

  return event;
}

// 生产环境使用动态「采样」
function getTraceOptions(isProd: boolean) {
  if (!isProd) {
    return { tracesSampleRate: 1.0 };
  }

  return {
    tracesSampler(samplingContext: TraceSamplingContext) {
      if (typeof samplingContext.parentSampled === 'boolean') {
        return samplingContext.parentSampled;
      }

      const op = samplingContext.transactionContext?.op;

      if (op === 'pageload') return 1.0;
      if (op === 'navigation') return 0.6;

      return 0.2;
    },
  };
}


console.log('[client] sentry init', environment);

Sentry.init({
  dsn: isLocalEnvironment ? undefined : dsns[environment],
  enabled: !isLocalEnvironment,  // 本地开发时禁用 sentry，避免干扰开发
  environment: environment,
  tunnel: '/monitoring',
  release: process.env.SCM_COMMIT_ID || undefined,
  ...getTraceOptions(isProdEnvironment),
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  profilesSampleRate: isProdEnvironment ? 0.1 : 0.5,

  //异常错误
  beforeSend(event) {

    // 可以在这里添加自定义逻辑,比如过滤敏感信息
    if (event.request) {
      // 移除敏感的 cookie 信息
      delete event.request.cookies
    }

    try {
      return addEnvironmentTags(event);
    } catch {
      return event;
    }
  },

  // 性能事务
  beforeSendTransaction(event) {
    try {
      if (typeof window === 'undefined') {
        return event;
      }

      // 添加环境标签
      addEnvironmentTags(event);

      // 为事务名称添加主机名前缀
      const hostname = window.location.hostname || 'unknown-host';
      if (typeof event.transaction === 'string' && event.transaction.length > 0) {
        event.transaction = `${hostname} ${event.transaction}`;
      }

      return event;
    } catch {
      return event;
    }
  },

  integrations: [
    Sentry.browserTracingIntegration({
      shouldCreateSpanForRequest(url) {
        // 排除 Sentry 自身请求,避免递归追踪
        if (url.includes('/monitoring')) return false;
        
        // // 排除 Next.js 静态资源和构建产物
        // if (url.includes('_next/static/')) return false;
        // if (url.includes('_next/image')) return false;
        
        // // 排除 _next/data/ 请求 - 这些请求已经被 navigation 事务追踪
        // // navigation 事务会包含整个导航过程,无需为数据请求单独创建 span
        // if (url.includes('_next/data/')) return false;
        
        // // 排除静态资源目录
        // if (url.includes('/static/')) return false;
        // if (url.includes('/assets/')) return false;
        
        // // 排除静态文件
        // if (/\.(png|jpg|jpeg|gif|svg|ico|webp|woff|woff2|ttf|eot|css|map)$/.test(url)) return false;
        
        return true;
      },
    }),
    Sentry.browserProfilingIntegration(), // chrome warn 不支持js profiling
    // Sentry.replayIntegration(), // 十分影响性能

    // 把 console.error 作为错误事件上报
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
  ].filter(Boolean),

  tracePropagationTargets: [
    /^\/(api|mallfe-api)\//,  // 所有同源请求
    /3jyx\.cn$/,
  ]
});


